<script setup lang="ts">
/**
 * 记录抽屉的公共骨架。
 *
 * 在这之前，项目页（ProjectSectionView）和模块中心（ModuleWorkbenchView）
 * 各写了一套抽屉，改一边另一边不动——Tony 2026-08-18 两次踩到
 * （「我上传的图片没有看到看执行清单」，那次他在项目页，被改过的只有模块中心）。
 *
 * 这里只放**两边都该有**的骨架：
 *   上下文 → 模块专属操作（插槽）→ 子条目 → 状态推进 → 变更历史 → 文件/附件 → 在线编辑
 * 天然只属于一边的东西不塞进来：跨项目列只在模块中心有意义，
 * 交付物审批、项目健康度只在项目页有意义——那些走 #status / #assist 插槽。
 *
 * 守的一条线：**抽公共骨架 + 留插槽，不追求「一个组件管所有」**。
 * 哪天某一块要加第 4 个 v-if 分支，那是信号：它该有自己的组件。
 */
import { computed, ref, watch } from 'vue';
import { File } from 'lucide-vue-next';

import { eqsApi } from '@/api/eqs';
import RecordChildList from '@/components/RecordChildList.vue';
import RecordHistory from '@/components/RecordHistory.vue';
import StatusChip from '@/components/StatusChip.vue';
import type { DrawerChildList, DrawerStatusAction } from '@/components/record-drawer';
import { confirmAction } from '@/services/confirm';
import { notify } from '@/services/notice';
import type { ModuleRecord } from '@/types/domain';
import { errorMessage, formatCell, formatDate } from '@/utils/format';

const props = withDefaults(defineProps<{
  record: ModuleRecord | null;
  /**
   * 文件区形态：
   * versions    = DMS 正式版本（文件模块，走 documents/:id/versions）
   * attachments = 业务附件（问题、变更、FMEA…… 走 module-tools 的附件接口）
   * none        = 这类记录不挂文件
   */
  fileMode?: 'versions' | 'attachments' | 'none';
  /** 文件区标题；不同子页措辞不同（项目文件版本 / 业务附件 / DFMEA 文件与更新历史） */
  fileLabel?: string;
  fileTitle?: string;
  fileHint?: string;
  /** 附件接口要的 module/resource；不传就从 record.__module/__resource 上取 */
  module?: string;
  resource?: string;
  /** 审计里记的 entity_type；null 表示这类记录查不到历史，那就别给按钮 */
  historyEntityType?: string | null;
  historyHint?: string;
  children?: DrawerChildList | null;
  statusActions?: DrawerStatusAction[];
  statusLabel?: string;
  statusTitle?: string;
  statusContext?: string;
  /** 没有可执行动作时说清楚为什么（例如关闭被质量口拦住） */
  statusEmptyHint?: string;
  /** 确认弹窗里指名道姓：「确认将“EC-2026-001”执行“批准”？」 */
  confirmSubject?: string;
  statusBusy?: boolean;
  writable?: boolean;
  /**
   * 只读时补一句为什么。
   * 两边原因不同：模块中心是「这个模块后端只开放了读」，项目页是「这个账号没有写权限」，
   * 所以不写死在骨架里——说错原因比不说更误导人。
   */
  readonlyHint?: string;
  /** 文件模块才有：OnlyOffice 在线编辑 */
  onlyOffice?: boolean;
  /**
   * 改子条目状态前先跑一下（通常是把抽屉表单里没保存的改动存了）。
   * 返回 false 放弃本次操作。见 RecordChildList 里的说明。
   */
  beforeChildAction?: () => Promise<boolean>;
}>(), {
  fileMode: 'none',
  fileLabel: 'BUSINESS ATTACHMENTS',
  fileTitle: '业务附件',
  fileHint: '实际内容以文件为准。再次上传会保留旧文件，形成可下载的更新历史；单文件最大 1GB。',
  module: '',
  resource: '',
  historyEntityType: null,
  historyHint: '',
  children: null,
  statusActions: () => [],
  statusLabel: 'NEXT STEP',
  statusTitle: '这条记录的下一步',
  statusContext: '',
  statusEmptyHint: '',
  confirmSubject: '',
  statusBusy: false,
  writable: true,
  readonlyHint: '',
  onlyOffice: false,
});

const emit = defineEmits<{
  /** 点了状态按钮；确认弹窗已经在这里问过，外面直接执行即可 */
  status: [action: DrawerStatusAction];
  /** 传了新文件；外面据此刷新列表（文件数、最后更新时间要跟着动） */
  'files-changed': [];
  /** 请求打开 OnlyOffice；全屏编辑器在视图层，因为抽屉自己会被关掉 */
  'open-editor': [];
}>();

const childList = ref<{
  reload?: () => void;
  $el?: { scrollIntoView?: (options?: ScrollIntoViewOptions) => void };
} | null>(null);

/**
 * 让外面能把子条目滚进视野、写完 D4 之后让它自己刷新。
 * 抽屉很长，「下一步」提示里的「看执行清单 ↓」就靠这个（Tony 2026-08-18）。
 */
defineExpose({
  reloadChildren: (): void => childList.value?.reload?.(),
  scrollToChildren: (): void =>
    childList.value?.$el?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }),
});

/** 附件接口要的 module/resource：显式传的优先，否则取记录自带的标识 */
const source = computed(() => ({
  module: props.module || String(props.record?.__module || ''),
  resource: props.resource || String(props.record?.__resource || ''),
}));

const fileInput = ref<HTMLInputElement | null>(null);
/*
  拖拽上传（0820：「最好可以做到拖拽上传」）。
  和 RecordFormDialog 同一招：drop 进来的文件塞进原生 input，
  后面的上传逻辑一行不改。现场拍的照片从桌面拖进来就行。
*/
const dragOver = ref(false);
function onFileDrop(event: DragEvent): void {
  dragOver.value = false;
  const dropped = event.dataTransfer?.files;
  if (!dropped?.length || !fileInput.value) return;
  const bag = new DataTransfer();
  for (const file of dropped) bag.items.add(file);
  fileInput.value.files = bag.files;
}
const fileNote = ref('');
const fileUploading = ref(false);
const filesLoading = ref(false);
const filesError = ref('');
const files = ref<Array<{
  id: number | string;
  fileName: string;
  sizeBytes: number;
  note?: string;
  uploadedBy?: string;
  createdAt?: string;
  mimeType?: string;
}>>([]);

const onlyOfficeStatus = ref<{ configured: boolean; editable: boolean } | null>(null);
const onlyOfficeError = ref('');

/*
  业务附件默认收起（0824 Tony：抽屉还是太长）。

  收的只有「佐证类附件」这一种。问题、变更、BOM 的抽屉里附件是**配角**：
  十次里九次人是来看正文、推状态的，一整块上传控件加已传清单常驻，
  等于拿最占地方的东西挡住最常用的东西。

  界线不看 fileMode 而看 fileLabel——**这是调用方自己说的话**：
    VERSION CONTROL     文件模块，这条记录本身就是这份文件
    PRIMARY FILE        DFMEA、控制计划这类质量文件，附件就是那份正文
    BUSINESS ATTACHMENTS 正文之外补的佐证
  质量文件走的也是 attachments 接口（它们不进 DMS 版本库），
  只按 fileMode 分就会把 DFMEA 的正文折起来——照着 fileMode 写过一版，
  正是 ProjectSectionView 的 DFMEA 用例把它拦下来的。

  用不着为此新开一个 prop：两个调用点早就把 fileLabel 传对了，
  再加一个「要不要折叠」的开关，是让每个调用点把同一件事重说一遍，
  还多一个能传错的地方。代价写在这儿——**改这行眼眉文字会顺手改掉折叠行为**；
  哪天真出现「叫 BUSINESS ATTACHMENTS 但要常驻」的场子，那时候再加 prop。
*/
const SUPPORTING_FILE_LABEL = 'BUSINESS ATTACHMENTS';
const filesCollapsible = computed(
  () => props.fileMode === 'attachments' && props.fileLabel === SUPPORTING_FILE_LABEL,
);
const filesOpen = ref(false);

/**
 * 收起时标题行上的数量。
 *
 * 0 也照写「（0）」——藏成一片空白，人会以为这类记录压根不能传附件。
 * 读失败时写「读取失败」而不是 0：替后端把「没有」和「没读到」说成同一件事，
 * 正是这套系统在「346 个文件只显示 50 个」上吃过的亏。
 */
const filesCountLabel = computed(() => {
  if (filesError.value) return '读取失败';
  if (filesLoading.value) return '读取中';
  return String(files.value.length);
});

function formatBytes(value: number): string {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / 1024 / 1024).toFixed(1)} MB`;
}

async function loadFiles(): Promise<void> {
  const record = props.record;
  if (!record || props.fileMode === 'none') return;
  filesLoading.value = true;
  filesError.value = '';
  try {
    if (props.fileMode === 'versions') {
      const payload = await eqsApi.fileVersions(record.id);
      files.value = payload.versions.map((version) => ({
        id: version.id,
        fileName: String(version.title || `文件版本 ${formatCell(version.versionNo)}`),
        sizeBytes: Number(version.sizeBytes || version.size_bytes || 0),
        note: version.versionNo === undefined ? undefined : `V${formatCell(version.versionNo)}`,
        uploadedBy: String(version.ownerName || version.owner_name || ''),
        createdAt: String(version.createdAt || version.created_at || ''),
      }));
      return;
    }
    const { module, resource } = source.value;
    if (!module || !resource) throw new Error('当前记录缺少文件资源标识');
    const payload = await eqsApi.moduleAttachments(module, resource, record.id);
    files.value = payload.attachments;
  } catch (caught) {
    files.value = [];
    filesError.value = errorMessage(caught);
  } finally {
    filesLoading.value = false;
  }
}

async function loadOnlyOffice(): Promise<void> {
  const record = props.record;
  if (!record || !props.onlyOffice) return;
  onlyOfficeError.value = '';
  onlyOfficeStatus.value = null;
  try {
    onlyOfficeStatus.value = await eqsApi.onlyOfficeStatus(record.id);
  } catch (caught) {
    onlyOfficeError.value = errorMessage(caught);
  }
}

/* 换一条记录就整块重置：上一条的附件清单留在界面上会被当成这条的 */
watch(() => [props.record?.id, props.fileMode], () => {
  fileNote.value = '';
  fileUploading.value = false;
  filesError.value = '';
  files.value = [];
  // 换记录也把附件区收回去：抽屉打开就该是短的，这一条不给它留例外
  filesOpen.value = false;
  if (fileInput.value) fileInput.value.value = '';
  void loadFiles();
  void loadOnlyOffice();
}, { immediate: true });

async function uploadFile(): Promise<void> {
  const record = props.record;
  const file = fileInput.value?.files?.[0];
  if (!record || !file) {
    notify('error', '请选择文件', '先选择需要上传的真实业务文件。');
    return;
  }
  const { module, resource } = source.value;
  if (props.fileMode === 'attachments' && (!module || !resource)) {
    notify('error', '无法上传文件', '当前记录缺少文件资源标识。');
    return;
  }
  if (!await confirmAction({ title: `确认上传“${file.name}”作为当前记录的新版本？` })) return;
  const payload = new FormData();
  payload.append('file', file);
  const note = fileNote.value.trim();
  if (note) payload.append(props.fileMode === 'versions' ? 'title' : 'note', note);
  fileUploading.value = true;
  try {
    if (props.fileMode === 'versions') await eqsApi.uploadFileVersion(record.id, payload);
    else await eqsApi.uploadModuleAttachment(module, resource, record.id, payload);
    notify('success', '文件上传成功', `${file.name} 已保存，之前上传的文件仍保留在历史清单中。`);
    fileNote.value = '';
    if (fileInput.value) fileInput.value.value = '';
    await Promise.all([loadFiles(), loadOnlyOffice()]);
    emit('files-changed');
  } catch (caught) {
    notify('error', '文件上传失败', errorMessage(caught));
  } finally {
    fileUploading.value = false;
  }
}

/**
 * 附件的直链。图片用它当 <img src>，浏览器带 cookie 就能取到，
 * 不用先 fetch 成 blob（下载本来就是同一个地址）。
 */
function fileUrl(fileId: number | string, inline = false): string {
  const record = props.record;
  if (!record) return '';
  if (props.fileMode === 'versions') return eqsApi.fileDownloadUrl(record.id, fileId);
  const { module, resource } = source.value;
  const base = eqsApi.moduleAttachmentDownloadUrl(module, resource, record.id, fileId);
  // inline=1：让浏览器直接显示而不是下载（图片和 PDF 才有效，0822 第 8 条）
  return inline ? `${base}${base.includes('?') ? '&' : '?'}inline=1` : base;
}

/** 这份附件能不能直接看：图片和 PDF 可以，别的只能下载 */
function canPreview(item: { mimeType?: string; fileName: string }): boolean {
  return isImage(item)
    || (item.mimeType ?? '').toLowerCase() === 'application/pdf'
    || /\.pdf$/iu.test(item.fileName);
}

function previewFile(item: { id: number | string }): void {
  window.open(fileUrl(item.id, true), '_blank', 'noopener');
}

const IMAGE_EXT = /\.(png|jpe?g|gif|webp|bmp|heic|heif)$/iu;

/** 是不是图片：优先看 MIME，老数据没有 MIME 就看扩展名 */
function isImage(item: { mimeType?: string; fileName: string }): boolean {
  return (item.mimeType ?? '').startsWith('image/') || IMAGE_EXT.test(item.fileName);
}

/*
  显示名。

  手机存下来的照片名常常是 `c73eddc3d214156087bd11d897d039f1.png` 这种哈希，
  挂在问题下面谁也认不出是哪张（0820 Tony：「名称和附件也不一样」）。
  规则：填了「文件说明」就拿说明当标题、原文件名退到第二行；
  没填说明而文件名又是一串哈希，就退回「图片 / 文件 + 上传日期」，别把哈希当标题。
*/
function displayName(item: { fileName: string; note?: string; createdAt?: string }): string {
  if (item.note?.trim()) return item.note.trim();
  const base = item.fileName.replace(/\.[^.]+$/u, '');
  const looksHashed = /^[0-9a-f]{16,}$/iu.test(base) || /^(IMG|image|微信图片|mmexport)[-_0-9]*$/iu.test(base);
  if (!looksHashed) return item.fileName;
  const day = item.createdAt ? formatDate(item.createdAt) : '';
  return `${IMAGE_EXT.test(item.fileName) ? '图片' : '文件'}${day ? ` · ${day}` : ''}`;
}

function downloadFile(fileId: number | string | undefined): void {
  const record = props.record;
  if (!record || fileId === undefined) return;
  if (props.fileMode === 'versions') {
    window.location.assign(eqsApi.fileDownloadUrl(record.id, fileId));
    return;
  }
  const { module, resource } = source.value;
  window.location.assign(
    eqsApi.moduleAttachmentDownloadUrl(module, resource, record.id, fileId));
}

async function runStatus(action: DrawerStatusAction): Promise<void> {
  if (props.statusBusy) return;
  const confirmed = await confirmAction({
    title: props.confirmSubject
      ? `确认将“${props.confirmSubject}”执行“${action.label}”？`
      : `确认执行“${action.label}”？`,
    message: action.confirmMessage ?? action.hint,
    danger: action.danger,
    confirmLabel: action.label,
  });
  if (!confirmed) return;
  emit('status', action);
}
</script>

<template>
  <div v-if="record" class="dialog-operations">
    <!-- 视图专属的上下文：内部 ID、跳去关联项目工作区…… -->
    <slot name="context" />

    <!-- 模块专属操作：问题的 5Why/8D、变更的执行清单、团队的组织架构 -->
    <slot name="assist" />

    <RecordChildList
      v-if="children"
      ref="childList"
      :module="children.module"
      :resource="children.resource"
      :parent-id="record.id"
      :title="children.title"
      :empty-hint="children.emptyHint"
      :extra-fields="children.extraFields"
      :editable="children.editable ?? writable"
      :step-options="children.stepOptions"
      :preset-step="children.presetStep ?? null"
      :before-advance="beforeChildAction"
    />

    <!-- 状态推进。交付物审批那种带审批人选择的走 #status 插槽整块换掉 -->
    <slot name="status">
      <section class="status-actions">
        <span>{{ statusLabel }}</span><h3>{{ statusTitle }}</h3>
        <p v-if="statusContext" class="status-context">{{ statusContext }}</p>
        <div v-if="statusActions.length" class="button-row">
          <button
            v-for="action in statusActions"
            :key="action.value"
            class="button"
            :class="action.danger ? 'button-danger' : 'button-secondary'"
            type="button"
            :disabled="statusBusy"
            @click="runStatus(action)"
          >{{ statusBusy ? '处理中…' : action.label }}</button>
        </div>
        <small
          v-for="action in statusActions.filter((item) => item.hint)"
          :key="`hint-${action.value}`"
          class="operation-hint"
        >{{ action.hint }}</small>
        <!-- 没有下一步时，能说清楚原因就说清楚，别只留一句「没有下一步」 -->
        <small v-if="!statusActions.length && statusEmptyHint" class="quality-gate">{{ statusEmptyHint }}</small>
        <small v-else-if="!statusActions.length" class="field-error">当前状态没有可执行的下一步。</small>
        <small v-if="!writable && readonlyHint" class="field-error">{{ readonlyHint }}</small>
      </section>
    </slot>

    <RecordHistory
      v-if="historyEntityType"
      :entity-type="historyEntityType"
      :entity-id="record.id"
      :hint="historyHint || undefined"
    />

    <section v-if="fileMode !== 'none'" class="file-operations">
      <!--
        附件默认收起，一行写清「叫什么 + 有几份」；文件模块的版本清单是抽屉正文，照旧铺开。
        做成有边框的按钮：一行没框的字看不出能点（0823 起全站的规矩）。
      -->
      <button
        v-if="filesCollapsible"
        class="files-toggle"
        type="button"
        :aria-expanded="filesOpen"
        @click="filesOpen = !filesOpen"
      >
        <i aria-hidden="true">{{ filesOpen ? '▾' : '▸' }}</i>
        <b>{{ fileTitle }}（{{ filesCountLabel }}）</b>
        <small>{{ filesOpen ? '收起' : '展开：查看与上传' }}</small>
      </button>
      <!-- 收起时也说一句放什么：只写「业务附件（1）」，人不知道该往里传什么（0828） -->
      <p v-if="filesCollapsible && !filesOpen" class="files-collapsed-hint">{{ fileHint }}</p>
      <template v-else>
        <span>{{ fileLabel }}</span>
        <h3>{{ fileTitle }}</h3>
      </template>

      <!-- 读不到清单的原因留在折叠外面：收起时只显示「（0）」等于替后端撒谎 -->
      <p v-if="filesError" class="field-error drawer-files-error">{{ filesError }}</p>

      <template v-if="!filesCollapsible || filesOpen">
        <p class="operation-hint">{{ fileHint }}</p>
        <label
          class="drawer-dropzone" :class="{ 'is-over': dragOver }"
          @dragover.prevent="dragOver = true"
          @dragleave.prevent="dragOver = false"
          @drop.prevent="onFileDrop"
        >
          <span>选择新文件 *</span>
          <input ref="fileInput" type="file" @change="dragOver = false" />
          <small class="drawer-dropzone-hint">
            {{ dragOver ? '松开即可放入' : '点上面选择，或把照片 / 文件拖到这里' }}
          </small>
        </label>
        <label>
          <span>文件说明（可选，会当成这份附件的标题）</span>
          <input v-model="fileNote" type="text" placeholder="例如：卡扣断裂位置照片">
        </label>
        <button
          class="button button-primary"
          type="button"
          :disabled="fileUploading"
          @click="uploadFile"
        >{{ fileUploading ? '正在上传，请勿关闭页面…' : '上传新版本' }}</button>
        <div class="version-list">
          <!-- 标题跟上面那块用同一个词，再带上数量：原来上面叫「业务附件」、下面叫「文件与历史版本」，
               同一件东西两个名字（0820 Tony 反馈）。
               折叠形态下这一行不写：上面那个开关已经写了「业务附件（3）」，
               再写一遍「已上传的业务附件（3）」就是这轮改动本来要消掉的重复。 -->
          <b v-if="!filesCollapsible">已上传的{{ fileTitle }}<em v-if="files.length">（{{ files.length }}）</em></b>
          <small v-if="filesLoading">正在读取文件清单…</small>
          <article v-for="item in files" :key="item.id" class="drawer-file">
            <!-- 图片直接出缩略图：手机存的照片名是一串哈希，看名字认不出是哪张，看图一眼就认得 -->
            <a
              v-if="isImage(item)" class="drawer-file-thumb"
              :href="fileUrl(item.id, true)" target="_blank" rel="noopener"
              :title="`打开大图：${item.fileName}`"
            ><img :src="fileUrl(item.id, true)" :alt="displayName(item)" loading="lazy"></a>
            <span v-else class="drawer-file-icon" aria-hidden="true"><File :size="22" color="currentColor" /></span>
            <span class="drawer-file-main">
              <b>{{ displayName(item) }}</b>
              <small>
                {{ formatBytes(item.sizeBytes) }}
                <template v-if="displayName(item) !== item.fileName"> · {{ item.fileName }}</template>
                <template v-if="item.uploadedBy"> · {{ item.uploadedBy }}</template>
                <template v-if="item.createdAt"> · {{ formatDate(item.createdAt) }}</template>
              </small>
            </span>
            <span class="drawer-file-acts">
              <!-- 能直接看的就先给「预览」：附件多半是现场照片，下下来再打开太绕（0822 第 8 条） -->
              <button
                v-if="canPreview(item)" class="inline-button" type="button"
                @click="previewFile(item)"
              >预览</button>
              <button class="inline-button" type="button" @click="downloadFile(item.id)">下载</button>
            </span>
          </article>
          <small v-if="!filesLoading && !files.length && !filesError">暂无文件，请上传实际业务文件。</small>
        </div>
      </template>
    </section>

    <section v-if="onlyOffice" class="file-operations">
      <span>ONLYOFFICE / ONLINE EDITING</span><h3>在线编辑服务</h3>
      <StatusChip
        v-if="onlyOfficeStatus"
        :value="onlyOfficeStatus.editable ? 'normal' : 'warning'"
        :label="onlyOfficeStatus.editable ? '可在线编辑' : onlyOfficeStatus.configured ? '尚无文件版本' : '配置不完整'"
      />
      <span v-else-if="onlyOfficeError" class="field-error" :title="onlyOfficeError">服务状态读取失败：{{ onlyOfficeError }}</span>
      <small v-else>正在读取服务状态…</small>
      <button
        class="button button-secondary"
        type="button"
        :disabled="!onlyOfficeStatus?.editable"
        @click="emit('open-editor')"
      >在线编辑</button>
    </section>
  </div>
</template>

<style scoped>
/*
  附件折叠开关。抽屉里别的区块的样式都在全局 styles.css 里，
  这一条只服务这个组件，就近放着——全局那张表已经够长了。
*/
.files-toggle {
  display: flex; align-items: baseline; gap: 8px; width: 100%;
  padding: 9px 12px;
  border: 1px solid var(--line); border-radius: var(--radius-s);
  background: var(--white); font: inherit; text-align: left; cursor: pointer;
}
.files-toggle:hover { border-color: var(--blue); }
.files-toggle > i { flex: 0 0 auto; color: var(--muted); font-style: normal; font-size: 11px; }
.files-toggle > b { flex: 1; min-width: 0; color: var(--ink); font-size: 14px; }
.files-toggle > small { flex: 0 0 auto; color: var(--muted); font-size: 12px; }
/* 全局 .file-operations 是 grid，<p> 自带的上下边距在这里只会撑出空行 */
.files-collapsed-hint { margin: 4px 0 0; color: var(--muted); font-size: 11px; line-height: 1.7; }
.drawer-files-error { margin: 0; }
</style>
